import { Gauge } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../lib/cn';
import type { UsageInfo } from '../lib/ws';

type Props = { usage: UsageInfo };

/**
 * Show context occupancy only when the provider reports a real
 * last-request count and window. Historical usage alone is not
 * enough to calculate how full the current context is.
 */
export function ContextMeter({ usage }: Props) {
  const [open, setOpen] = useState(false);
  const context =
    typeof usage.contextTokens === 'number' &&
    Number.isFinite(usage.contextTokens) &&
    usage.contextTokens >= 0 &&
    typeof usage.contextWindow === 'number' &&
    Number.isFinite(usage.contextWindow) &&
    usage.contextWindow > 0
      ? { tokens: usage.contextTokens, window: usage.contextWindow }
      : null;
  const pct = context ? Math.min(100, Math.round((context.tokens / context.window) * 100)) : null;
  const warn = pct !== null && pct >= 75;
  const crit = pct !== null && pct >= 90;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full border border-border px-3 text-[12px]',
          'hover:bg-subtle',
          crit && 'border-destructive/40 text-destructive',
          warn && !crit && 'border-warning/40 text-warning',
        )}
        title={
          context
            ? `Контекст: ${formatTokens(context.tokens)} из ${formatTokens(context.window)}`
            : 'Заполнение контекста неизвестно'
        }
        aria-label={pct === null ? 'Заполнение контекста неизвестно' : `Контекст занят на ${pct}%`}
      >
        <Gauge size={12} />
        <span className="hidden md:inline">Контекст</span>
        <span className="font-mono tabular-nums">{pct === null ? '—' : `${pct}%`}</span>
      </button>
      {open && (
        <div className="mc-menu-enter origin-top-right absolute top-[calc(100%+4px)] right-0 z-20 w-[min(calc(100vw-2rem),260px)] rounded-2xl border border-border/70 bg-surface p-3 text-[12px] mc-float">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-medium">Контекст</span>
            <span className="font-mono tabular-nums">
              {context
                ? `${formatTokens(context.tokens)} / ${formatTokens(context.window)}`
                : 'Нет точных данных'}
            </span>
          </div>
          {context && pct !== null && (
            <div
              className="mb-2 h-1.5 overflow-hidden rounded-full bg-subtle"
              role="progressbar"
              aria-label="Заполнение контекста"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              tabIndex={0}
            >
              <div
                className={cn(
                  'mc-meter-fill h-full w-full origin-left rounded-full',
                  crit ? 'bg-destructive' : warn ? 'bg-warning' : 'bg-primary',
                )}
                style={{ transform: `scaleX(${pct / 100})` }}
              />
            </div>
          )}
          {context && (
            <p className="mb-2 text-[11.5px] text-muted-foreground">
              Учтено в диалоге: {formatTokens(usage.totalTokens)}. Кэш — часть входных токенов.
            </p>
          )}
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground">
            <Row k="Вход" v={usage.inputTokens} />
            <Row k="Выход" v={usage.outputTokens} />
            <Row k="Из кэша" v={usage.cacheReadInputTokens} />
            <Row k="В кэш" v={usage.cacheCreationInputTokens} />
            {usage.costUsd !== null && (
              <>
                <dt>Стоимость</dt>
                <dd className="font-mono tabular-nums text-foreground">
                  ${usage.costUsd.toFixed(4)}
                </dd>
              </>
            )}
            {usage.model && (
              <>
                <dt>Модель</dt>
                <dd className="truncate font-mono text-foreground">{usage.model}</dd>
              </>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: number }) {
  return (
    <>
      <dt>{k}</dt>
      <dd className="font-mono tabular-nums text-foreground">{formatTokens(v)}</dd>
    </>
  );
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}
